import { ArrowRight } from "lucide-react";
import { BRAND } from "../lib/brand";
import useReveal from "../hooks/useReveal";

const Hero = () => {
    const leftRef = useReveal();
    const rightRef = useReveal();

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center bg-navy text-cream overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 paper-grain opacity-40 pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-gold/5 blur-3xl" />
                <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-gold/[0.04] blur-3xl" />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-20 lg:pt-28 lg:pb-24 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Copy */}
                    <div ref={leftRef} className="reveal lg:col-span-7 order-2 lg:order-1">
                        <div className="flex items-center gap-4 mb-8">
                            <span className="block w-16 h-px bg-gold" />
                            <span className="eyebrow text-gold">Cirugía · Laparoscopia · Obesidad</span>
                        </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem] leading-[1.02] tracking-tight text-cream"
                    >
                            Cirugía de precisión.
                            <br />
                            <span className="italic text-gold-soft">Resultados</span> que
                            transforman.
                        </h1>

                        <p
                            data-testid="hero-subheadline"
                            className="mt-8 text-base sm:text-lg max-w-2xl text-cream/75 font-light leading-relaxed"
                        >
                            <span className="font-medium text-cream">
                                Dr. Erik E. Cárdenas Hernández
                            </span>{" "}
                            · Cirujano General y Laparoscopista Avanzado · Especialista en Manejo
                            Integral de la Obesidad.
                        </p>

                        <div className="mt-12 flex flex-col sm:flex-row gap-4">
                            <button
                                data-testid="hero-primary-cta"
                                onClick={() => scrollTo("contacto")}
                                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gold text-navy text-xs sm:text-sm tracking-[0.22em] uppercase font-semibold hover:bg-gold-soft transition-colors duration-300"
                            >
                                Agenda tu consulta
                                <ArrowRight
                                    size={16}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </button>
                            <button
                                data-testid="hero-secondary-cta"
                                onClick={() => scrollTo("punto-cero")}
                                className="group inline-flex items-center justify-center gap-3 px-8 py-4 border border-cream/40 text-cream text-xs sm:text-sm tracking-[0.22em] uppercase font-light hover:border-gold hover:text-gold transition-all duration-300"
                            >
                                Conoce el Protocolo Punto Cero
                            </button>
                        </div>

                        {/* Credential badges */}
                        <div className="mt-16 flex flex-wrap gap-x-8 gap-y-3 text-[11px] tracking-[0.24em] uppercase text-cream/60">
                            <span className="flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-gold" />
                                YFACS · American College of Surgeons
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-gold" />
                                MASPEN
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-gold" />
                                Universidad Anáhuac
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-gold" />
                                U.D.E.F.A. · Hospital Central Militar
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-gold" />
                                Stanford / IRCAD · Advance Laparoscopy
                            </span>
                        </div>
                    </div>

                    {/* Headshot */}
                    <div
                        ref={rightRef}
                        className="reveal lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end"
                    >
                        <div className="relative crest-frame w-full max-w-[420px]">
                            <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-gold/30 pointer-events-none" />
                            <img
                                data-testid="hero-headshot"
                                src={BRAND.headshot}
                                alt="Foto profesional Dr. Cárdenas"
                                className="relative w-full aspect-[4/5] object-cover grayscale-[0.15] contrast-[1.05]"
                                loading="eager"
                            />
                            <div className="absolute -bottom-4 left-6 right-6 flex items-center justify-between bg-navy-deep border border-gold/20 px-4 py-3">
                                <span className="font-display text-cream text-base lg:text-lg leading-tight">
                                    Dr. Erik E. Cárdenas Hernández
                                </span>
                                <span className="text-[10px] tracking-[0.25em] uppercase text-gold whitespace-nowrap ml-3">
                                    YFACS · MASPEN
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll cue */}
                <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-3 text-cream/40 text-[10px] tracking-[0.4em] uppercase">
                    <span>Scroll</span>
                    <span className="w-px h-10 bg-gradient-to-b from-gold/60 to-transparent" />
                </div>
            </div>
        </section>
    );
};

export default Hero;
